import React, { useEffect } from "react"
import { useTranslation } from "react-i18next"
import defaultStyle from "../../../styles/defaultPageStyle.module.css"
import { TitlePageResponsiveIconSmall } from "../../../components/Titles/Titles"
import Button, { ButtonIcon } from "../../../components/Button/Button"
import { faClose, faFileSignature, faPercent, faTicket } from "@fortawesome/free-solid-svg-icons"
import InputField from "../../../components/InputField/InputField"
import { GenericDropDown } from "../../../components/Dropdown/Dropdown"
import { EEnableStatusType } from "../../../Enum/EEnableStatusType"
import Animate, { AnimationTypes } from "../../../components/Animate/Animate"
import ModalPortal from "../../../components/Modal/ModalPortal"
import THEME from "../../../configuration/Theme"
import { SIZE } from "../../../configuration/Sizes"
import GetSpace from "../../../utils/space"
import { CouponEntity } from "../../../Objects/Coupon/CouponEntity"
import { useSaveCoupon } from "../../../hooks/Coupon/useSaveCoupon"
import { useGetCoupon } from "../../../hooks/Coupon/useGetCoupon"
import { useCreateCoupon } from "../../../hooks/Coupon/useCreateCoupon"
import { usePermission } from "../../../hooks/Permissions/usePermission"
import { Permissions } from "../../../Enum/PermissionsType"
import { SimpleToggle } from "../../../components/SimpleToggle/SimpleToggle"
import { CopyDraftAlert } from "../../../components/CopyDraftAlert/CopyDraftAlert"
import { useRegister } from "../../../hooks/Register/useRegister"
import { ViewRegister } from "../../../components/ViewRegister/ViewRegister"
import { useConfirmation } from "../../../context/useConfirmationContext"
import { useRemoveCoupon } from "../../../hooks/Coupon/useRemoveCoupon"
import { CanView } from "../../../components/CanView/CanView"
import { PopupHelpButton } from "../../../components/Help/PopupHelpButton"
import { useError } from "../../../context/useErrorContext"

interface CouponsPupopProbs {
  title: string
  coupon?: CouponEntity
  onClonse: () => void
  onCopy?: () => void
}

export const CouponsPupop: React.FC<CouponsPupopProbs> = ({ title, coupon, onClonse, onCopy }) => {
  const { t } = useTranslation("frontend")

  const isCopiedDraft = coupon?.id === -1

  const canViewRegister = !!coupon && coupon.id !== -1

  const { handleEnableRegister, handleDisableRegister, isRegisterEnable } = useRegister()

  const { setNewConfirmation } = useConfirmation()

  const { setError } = useError()

  const { handleRemove } = useRemoveCoupon()

  const {
    status,
    code,
    discountPercentage,
    hasPerCustomerUsageLimit,
    maxUsagePerCustomer,
    hasGlobalUsageLimit,
    maxUsageTotal,
    setHasPerCustomerUsageLimit,
    setMaxUsagePerCustomer,
    setHasGlobalUsageLimit,
    setMaxUsageTotal,
    handleStatusChange,
    handleCodeChange,
    handleDiscountPercentageChange,
    handleSubmitForm,
    handleInitialValue,
  } = useCreateCoupon()

  const canEdite = !usePermission(Permissions.CanEditeCoupons)

  const { handleSaveCoupon } = useSaveCoupon()

  const { handleGetCoupon } = useGetCoupon()

  useEffect(() => {
    if (!coupon) {
      return
    }

    handleInitialValue(coupon)
  }, [coupon])

  const handleSubmit = (event: React.FormEvent) => {
    event.preventDefault()

    const newCupom = handleSubmitForm()

    if (!Number.isFinite(newCupom.discountPercentage) || newCupom.discountPercentage <= 0) {
      setError(t("general:label_invalid"), t("coupons_popup.discount_percentage_must_be_greater_than_zero"))

      return
    }

    handleSaveCoupon(newCupom, () => {
      handleGetCoupon(onClonse)
    })
  }

  return (
    <ModalPortal>
      <div className={defaultStyle.overlay}>
        <Animate type={AnimationTypes.slideUpIn} duration="0.3s">
          <div className={defaultStyle.alert1}>
            <TitlePageResponsiveIconSmall
              title={title}
              icon={faTicket}
              iconColor={THEME.colors.iconBGColor}
              children={
                <div className={defaultStyle.ButtonsActions}>
                  {coupon && coupon.id !== -1 && (
                    <div style={{ width: "100px", marginRight: "5px" }}>
                      <GenericDropDown
                        data={Object.mapEnumToStrings(EEnableStatusType, {
                          exclude: [EEnableStatusType.All],
                        })}
                        disable={canEdite}
                        initialState={status}
                        handlerFuncion={handleStatusChange}
                        descriptionKey="interactionDescriptions:pages.serivesDir.coupons.couponsPupop.select.select"
                      />

                      <GetSpace right={10} />
                    </div>
                  )}

                  <PopupHelpButton
                    helpKey="pages.serivesdir.coupons.couponspupop.1"
                    variant="global"
                    title={t("helpPopup:global.coupon.title")}
                    description={t("helpPopup:global.coupon.description")}
                    bullets={t("helpPopup:global.coupon.bullets", { returnObjects: true }) as string[]}
                  />

                  <GetSpace right={5} />

                  <ButtonIcon
                    sizeNumber={SIZE.buttonCloseIconSize}
                    bgColor={THEME.colors.buttonBGCloseColor}
                    iconColor={THEME.colors.buttonFGCloseColor}
                    icon={faClose}
                    onClick={onClonse}
                    size="small"
                    descriptionKey="interactionDescriptions:pages.serivesDir.coupons.couponsPupop.button.onClonse"
                  />
                </div>
              }
            />

            {isCopiedDraft && <CopyDraftAlert />}

            <form onSubmit={handleSubmit}>
              <section className="p-2">
                <InputField
                  type="text"
                  value={code}
                  icon={faFileSignature}
                  placeholder={t("coupons_popup.coupon_code_placeholder")}
                  onChange={handleCodeChange}
                  disable={canEdite}
                  required
                  descriptionKey="interactionDescriptions:pages.serivesDir.coupons.couponsPupop.input.couponCodePlaceholder"
                />

                <GetSpace top={10} />

                <InputField
                  type="number"
                  value={discountPercentage}
                  icon={faPercent}
                  disable={canEdite}
                  placeholder={t("coupons_popup.discount_percentage_placeholder")}
                  onChange={handleDiscountPercentageChange}
                  required
                  descriptionKey="interactionDescriptions:pages.serivesDir.coupons.couponsPupop.input.discountPercentagePlaceholder"
                />

                <div className={defaultStyle.divisionBar}></div>

                <SimpleToggle
                  title={t("coupons_popup.limit_per_customer")}
                  value={hasPerCustomerUsageLimit}
                  boxShadow="none"
                  disabled={canEdite}
                  onChange={(e) => setHasPerCustomerUsageLimit(e)}
                  descriptionKey="interactionDescriptions:pages.serivesDir.coupons.couponsPupop.toggle.limitPerCustomer"
                />

                {hasPerCustomerUsageLimit && (
                  <>
                    <GetSpace top={8} />

                    <InputField
                      type="number"
                      value={maxUsagePerCustomer}
                      icon={faTicket}
                      disable={canEdite}
                      placeholder={t("coupons_popup.max_usage_per_customer")}
                      onChange={(event) => setMaxUsagePerCustomer(event.target.value)}
                      required
                      descriptionKey="interactionDescriptions:pages.serivesDir.coupons.couponsPupop.input.maxUsagePerCustomer"
                    />
                  </>
                )}

                <div className={defaultStyle.divisionBar}></div>

                <SimpleToggle
                  title={t("coupons_popup.global_usage_limit")}
                  value={hasGlobalUsageLimit}
                  boxShadow="none"
                  disabled={canEdite}
                  onChange={(e) => setHasGlobalUsageLimit(e)}
                  descriptionKey="interactionDescriptions:pages.serivesDir.coupons.couponsPupop.toggle.globalUsageLimit"
                />

                {hasGlobalUsageLimit && (
                  <>
                    <GetSpace top={8} />

                    <InputField
                      type="number"
                      value={maxUsageTotal}
                      icon={faTicket}
                      disable={canEdite}
                      placeholder={t("coupons_popup.max_total_usage")}
                      onChange={(event) => setMaxUsageTotal(event.target.value)}
                      required
                      descriptionKey="interactionDescriptions:pages.serivesDir.coupons.couponsPupop.input.maxTotalUsage"
                    />
                  </>
                )}
              </section>

              <div className={defaultStyle.divisionBar}></div>

              <section className={defaultStyle.buttonsController}>
                <CanView permission={Permissions.CanEditeCoupons}>
                  <Button
                    label={t("buttons:button_save")}
                    bgColor={THEME.colors.buttonBGSaveColor}
                    typeButton="submit"
                    type="primary"
                    size="medium"
                    descriptionKey="interactionDescriptions:pages.serivesDir.coupons.couponsPupop.button.save"
                  />
                </CanView>

                {canViewRegister && (
                  <>
                    <GetSpace right={5} />

                    <Button
                      label={t("buttons:button_register")}
                      typeButton="button"
                      type="primary"
                      size="medium"
                      bgColor={THEME.colors.buttonBGRegisterColor}
                      onClick={handleEnableRegister}
                      descriptionKey="interactionDescriptions:pages.serivesDir.coupons.couponsPupop.button.register"
                    />
                  </>
                )}

                <CanView permission={Permissions.CanEditeCoupons}>
                  {coupon && coupon.id !== -1 && onCopy && (
                    <>
                      <GetSpace right={5} />

                      <Button
                        label={t("buttons:button_copy")}
                        typeButton="button"
                        type="primary"
                        size="medium"
                        bgColor={THEME.colors.buttonBGPlusColor}
                        onClick={onCopy}
                        descriptionKey="interactionDescriptions:pages.serivesDir.coupons.couponsPupop.button.copy"
                      />
                    </>
                  )}
                </CanView>

                {coupon && coupon.id !== -1 && (
                  <CanView permission={Permissions.CanEditeCoupons}>
                    <>
                      <GetSpace right={5} />

                      <Button
                        label={t("buttons:button_remove")}
                        typeButton="button"
                        type="primary"
                        size="medium"
                        iconColor={THEME.colors.buttonFGRemoveColor}
                        bgColor={THEME.colors.buttonBGRemoveColor}
                        onClick={() =>
                          setNewConfirmation(
                            t("general:label_title_remove"),
                            t("general:label_description_remove"),
                            async () => {
                              await handleRemove(coupon, onClonse)
                            },
                          )
                        }
                        descriptionKey="interactionDescriptions:pages.serivesDir.coupons.couponsPupop.button.remove"
                      />
                    </>
                  </CanView>
                )}
              </section>
            </form>

            {isRegisterEnable && coupon && (
              <ViewRegister id={coupon.id} typeEntity="CouponEntityId" onClose={handleDisableRegister} />
            )}
          </div>
        </Animate>
      </div>
    </ModalPortal>
  )
}
